import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './hooks/useAuth';
import { DarkModeProvider } from './hooks/useDarkMode';
import { ProtectedRoute, AdminProtectedRoute } from './components';
import {
  LoginPage,
  HomePage,
  AdminLoginPage,
  AdminDashboard,
  ShippersPage,
  ConsigneesPage,
  PortsPage,
  ShippingLinesPage,
  MakesPage,
  ModelsPage,
  CompaniesPage,
  InsurancePricesPage,
  VoyagesPage,
  CarEntryPage,
  VoyageDetailsPage,
  StatisticsVoyagePage,
  ChangeVoyageChassisPage,
  TotalCarsPage,
  BulkChangeVoyagePage,
  BulkLineMeterPage,
  BulkAddCarsPage
} from './pages';
import { ParkingListPage, LoadingListPage, InventoryPrintPage, VoyageStatisticsPrintPage, FreightManifestPrintPage, FreightManifestPage, BillOfLadingPage, BillOfLadingPrintPage, InvoicePage, BookingConfirmationPage } from './prints';

function App() {
  return (
    <Router>
      <DarkModeProvider>
        <AuthProvider>
          <div className="App bg-white dark:bg-gray-900 min-h-screen transition-colors duration-200">
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/admin" element={<AdminLoginPage />} />
            
            {/* Protected routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <HomePage />
                </ProtectedRoute>
              }
            />
            
            {/* Admin protected routes */}
            <Route
              path="/admin/dashboard"
              element={
                <AdminProtectedRoute>
                  <AdminDashboard />
                </AdminProtectedRoute>
              }
            />
            
            {/* Records management routes */}
            <Route
              path="/shippers"
              element={
                <ProtectedRoute>
                  <ShippersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/consignees"
              element={
                <ProtectedRoute>
                  <ConsigneesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ports"
              element={
                <ProtectedRoute>
                  <PortsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/shipping-lines"
              element={
                <ProtectedRoute>
                  <ShippingLinesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/makes"
              element={
                <ProtectedRoute>
                  <MakesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/models"
              element={
                <ProtectedRoute>
                  <ModelsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/companies"
              element={
                <ProtectedRoute>
                  <CompaniesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/insurance-prices"
              element={
                <ProtectedRoute>
                  <InsurancePricesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/voyages"
              element={
                <ProtectedRoute>
                  <VoyagesPage title="Total Voyages" isBooking={false} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bookings"
              element={
                <ProtectedRoute>
                  <VoyagesPage title="Total Bookings" isBooking={true} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/archived-voyages"
              element={
                <ProtectedRoute>
                  <VoyagesPage title="Archived Voyages" readonly={true} isArchived={true} isBooking={false} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/archived-bookings"
              element={
                <ProtectedRoute>
                  <VoyagesPage title="Archived Bookings" readonly={true} isArchived={true} isBooking={true} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/archived-cars"
              element={
                <ProtectedRoute>
                  <TotalCarsPage title="Archived Cars" isArchived={true} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/car-entry/:voyageId/:carId?"
              element={
                <ProtectedRoute>
                  <CarEntryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/voyage-details/:voyageId"
              element={
                <ProtectedRoute>
                  <VoyageDetailsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/statistics-voyage"
              element={
                <ProtectedRoute>
                  <StatisticsVoyagePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/change-voyage-chassis"
              element={
                <ProtectedRoute>
                  <ChangeVoyageChassisPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bulk-change-voyage"
              element={
                <ProtectedRoute>
                  <BulkChangeVoyagePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bulk-line-meter"
              element={
                <ProtectedRoute>
                  <BulkLineMeterPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bulk-add-cars/:voyageId?"
              element={
                <ProtectedRoute>
                  <BulkAddCarsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/total-cars"
              element={
                <ProtectedRoute>
                  <TotalCarsPage />
                </ProtectedRoute>
              }
            />
            
            {/* Print routes */}
            <Route
              path="/print/parking-list"
              element={
                <ProtectedRoute>
                  <ParkingListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/loading-list"
              element={
                <ProtectedRoute>
                  <LoadingListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/freight-manifest"
              element={
                <ProtectedRoute>
                  <FreightManifestPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bill-of-lading"
              element={
                <ProtectedRoute>
                  <BillOfLadingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoice"
              element={
                <ProtectedRoute>
                  <InvoicePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/booking-confirmation"
              element={
                <ProtectedRoute>
                  <BookingConfirmationPage />
                </ProtectedRoute>
              }
            />
            {/* Print-only routes (unprotected) for headless PDF rendering */}
            <Route path="/print/inventory" element={<InventoryPrintPage />} />
            <Route path="/print/voyage-statistics" element={<VoyageStatisticsPrintPage />} />
            <Route path="/print/freight-manifest" element={<FreightManifestPrintPage />} />
            <Route path="/print/bill-of-lading" element={<BillOfLadingPrintPage />} />
            
            {/* Redirect any unknown routes to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </div>
        </AuthProvider>
      </DarkModeProvider>
    </Router>
  );
}

export default App;
